import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowDownLeft, ArrowUpRight, Wallet, Repeat, CalendarClock, AlertTriangle, Plus, FileUp, Lightbulb, ReceiptText, Hourglass, Bell, ChevronDown, Camera, ChevronRight, Car, HandCoins, HardHat } from "lucide-react";
import { Button } from "../components/ui/button";
import TransactionDialog from "../components/TransactionDialog";
import ProjectionCard from "../components/ProjectionCard";
import api, { fmtNum, fmtDate } from "../lib/api";

const plHr = (n, one, few, many) => {
  if (n % 10 === 1 && n % 100 !== 11) return one;
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return few;
  return many;
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [review, setReview] = useState(null);
  const [ending, setEnding] = useState([]);
  const [fleet, setFleet] = useState(null);
  const [idleVehicles, setIdleVehicles] = useState([]);
  const [recv, setRecv] = useState(null);
  const [salary, setSalary] = useState(null);
  const [txOpen, setTxOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(null);

  const load = () => api.get("/dashboard").then((r) => setData(r.data));

  useEffect(() => {
    load();
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/review-counts").then((r) => setReview(r.data)).catch(() => {});
    api.get("/recurring/ending").then((r) => setEnding(r.data.items)).catch(() => {});
    api.get("/vehicles/profit", { params: { year: new Date().getFullYear() } }).then((r) => setFleet(r.data)).catch(() => {});
    api.get("/vehicles/idle").then((r) => setIdleVehicles(r.data.items)).catch(() => {});
    api.get("/receivables").then((r) => setRecv(r.data)).catch(() => {});
    api.get("/salary-reminder").then((r) => setSalary(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-[#475569]">Učitavanje...</p>;

  const alerts = [];
  if (data.alert?.breach) {
    alerts.push({
      sev: "red", icon: AlertTriangle, to: "/dnevni-saldo", testid: "notif-threshold",
      text: `Projekcija salda pada ispod praga ${fmtNum(data.alert.threshold, "€")} — prvi pad ${fmtDate(data.alert.first_breach_date)}, najniže ${fmtNum(data.alert.min_projected_eur, "€")} (${fmtDate(data.alert.min_date)})`,
    });
  }
  if (ending.length > 0) {
    alerts.push({
      sev: "red", icon: Hourglass, to: "/fiksni-troskovi", testid: "notif-ending",
      text: `${ending.length} ${plHr(ending.length, "fiksna stavka je", "fiksne stavke su", "fiksnih stavki je")} pri kraju rata (${ending.slice(0, 3).map((e) => e.name).join(", ")}${ending.length > 3 ? "..." : ""}) — obnoviti ili ukinuti?`,
    });
  }
  if (data.recurring_pending > 0) {
    alerts.push({
      sev: "amber", icon: Repeat, to: "/fiksni-troskovi", testid: "notif-recurring",
      text: `${data.recurring_pending} ${plHr(data.recurring_pending, "fiksna stavka još nije prebačena", "fiksne stavke još nisu prebačene", "fiksnih stavki još nije prebačeno")} u planirane transakcije za ovaj mjesec`,
    });
  }
  if (review?.smart_suggestions > 0) {
    alerts.push({
      sev: "blue", icon: Lightbulb, to: "/fiksni-troskovi", testid: "notif-suggestions",
      text: `${review.smart_suggestions} ${plHr(review.smart_suggestions, "pametni prijedlog čeka", "pametna prijedloga čekaju", "pametnih prijedloga čeka")} vašu odluku`,
    });
  }
  if (review?.pending_invoices > 0) {
    alerts.push({
      sev: "blue", icon: ReceiptText, to: "/racuni", testid: "notif-invoices",
      text: `${review.pending_invoices} ${plHr(review.pending_invoices, "račun čeka", "računa čekaju", "računa čeka")} pregled i spremanje`,
    });
  }
  if (recv?.overdue_count > 0) {
    alerts.push({
      sev: "red", icon: HandCoins, to: "/nenaplaceno", testid: "notif-receivables",
      text: `${recv.overdue_count} ${plHr(recv.overdue_count, "izlazni račun kasni", "izlazna računa kasne", "izlaznih računa kasni")} s naplatom — ukupno ${fmtNum(recv.overdue_total_eur, "€")}, najviše ${recv.max_days_overdue} ${recv.max_days_overdue % 10 === 1 && recv.max_days_overdue % 100 !== 11 ? "dan" : "dana"}`,
    });
  }
  if (salary?.show) {
    alerts.push({
      sev: salary.deadline_passed ? "red" : "amber", icon: HardHat, to: "/radnici", testid: "notif-salary",
      text: `Plaća za ${salary.prev_month} nije evidentirana za ${salary.missing.length} ${plHr(salary.missing.length, "radnika", "radnika", "radnika")} (${salary.missing.slice(0, 4).join(", ")}${salary.missing.length > 4 ? "..." : ""}) — ${salary.deadline_passed ? "rok (15.) je prošao: spojite isplate s izvodom ili ih povežite ručno" : `rok je ${salary.deadline}${salary.days_left > 0 ? `, još ${salary.days_left} ${plHr(salary.days_left, "dan", "dana", "dana")}` : ""}`}`,
    });
  }
  if (idleVehicles.length > 0) {
    alerts.push({
      sev: "amber", icon: Car, to: "/vozila", testid: "notif-idle-vehicles",
      text: `${idleVehicles.length} ${plHr(idleVehicles.length, "vozilo je dugo bez najma", "vozila su dugo bez najma", "vozila je dugo bez najma")}, a troškovi teku (${idleVehicles.slice(0, 3).map((v) => v.plate).join(", ")}${idleVehicles.length > 3 ? "..." : ""})`,
    });
  }
  const warnCount = alerts.filter((a) => a.sev !== "blue").length;
  const reviewCount = alerts.length - warnCount;
  const hasRed = alerts.some((a) => a.sev === "red");
  const expanded = notifOpen == null ? hasRed : notifOpen;
  const SEV_CLS = { red: "text-[#DC2626] bg-[#DC2626]/10", amber: "text-[#D97706] bg-[#D97706]/10", blue: "text-[#0D9488] bg-[#0D9488]/10" };

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Pregled</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Stanje računa</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="dashboard-add-tx-btn" onClick={() => setTxOpen(true)} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Nova transakcija
          </Button>
          <Button asChild variant="outline" className="rounded-sm border-[#CBD5E1] bg-white">
            <Link to="/racuni" data-testid="dashboard-invoice-link">
              <Camera className="w-4 h-4 mr-2" /> Slikaj račun
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-sm border-[#CBD5E1] bg-white">
            <Link to="/uvoz" data-testid="dashboard-import-link">
              <FileUp className="w-4 h-4 mr-2" /> Uvoz izvoda
            </Link>
          </Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div data-testid="notification-center" className={`bg-white border rounded-sm overflow-hidden ${hasRed ? "border-[#DC2626]/40" : "border-[#E2E8F0]"}`}>
          <button
            data-testid="notification-center-toggle"
            onClick={() => setNotifOpen(!expanded)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#F8FAFC] transition-colors duration-200"
          >
            <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${hasRed ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-[#0D9488]/10 text-[#0D9488]"}`}>
              <Bell className="w-4 h-4" />
            </span>
            <p className="text-sm font-semibold text-[#0F172A] min-w-0 truncate" data-testid="notification-summary">
              {warnCount > 0 && `${warnCount} ${plHr(warnCount, "upozorenje", "upozorenja", "upozorenja")}`}
              {warnCount > 0 && reviewCount > 0 && " · "}
              {reviewCount > 0 && `${reviewCount} za pregled`}
            </p>
            <ChevronDown className={`w-4 h-4 text-[#64748B] ml-auto shrink-0 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
          </button>
          {expanded && (
            <div className="border-t border-[#F1F5F9] divide-y divide-[#F1F5F9]">
              {alerts.map((a) => {
                const Icon = a.icon;
                return (
                  <Link key={a.testid} to={a.to} data-testid={a.testid} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] transition-colors duration-200">
                    <span className={`w-6 h-6 rounded-sm flex items-center justify-center shrink-0 ${SEV_CLS[a.sev]}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="min-w-0">{a.text}</span>
                    <ChevronRight className="w-4 h-4 text-[#CBD5E1] ml-auto shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#0D9488] text-white rounded-sm p-5" data-testid="total-balance-card">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4 opacity-70" />
            <p className="text-xs uppercase tracking-[0.15em] opacity-70">Ukupno EUR</p>
          </div>
          <p className="font-heading text-3xl font-bold tabular-nums">{fmtNum(data.total_eur, "€")}</p>
          <p className="text-xs opacity-60 mt-1 tabular-nums">{fmtNum(data.total_bam, "KM")} · tečaj {data.fx_rate}</p>
        </div>
        {data.banks.map((b) => (
          <div key={b.id} className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid={`bank-card-${b.short}`}>
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-3 truncate">{b.name}</p>
            <p className="font-heading text-2xl font-bold text-[#0F172A] tabular-nums">{fmtNum(b.total_eur, "€")}</p>
            <div className="flex gap-4 mt-2 text-xs text-[#475569] tabular-nums">
              <span>EUR: {fmtNum(b.eur_balance)}</span>
              <span>BAM: {fmtNum(b.bam_balance)}</span>
            </div>
          </div>
        ))}
      </div>

      <ProjectionCard />

      {data.planned?.count > 0 && (
        <div data-testid="planned-summary-card" className="bg-white border border-dashed border-[#D97706] rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-4 h-4 text-[#D97706]" />
            <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Planirani cash flow ({data.planned.count} {data.planned.count === 1 ? "stavka" : "stavki"})</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Planirani priljev</p>
              <p className="font-heading text-xl font-bold text-[#0D9488] tabular-nums">+{fmtNum(data.planned.in_eur, "€")}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Planirani odljev</p>
              <p className="font-heading text-xl font-bold text-[#DC2626] tabular-nums">−{fmtNum(data.planned.out_eur, "€")}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Projicirani saldo</p>
              <p className="font-heading text-xl font-bold text-[#D97706] tabular-nums" data-testid="projected-total">{fmtNum(data.planned.projected_total_eur, "€")}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Nadolazeće</p>
              {data.planned.upcoming.slice(0, 3).map((t) => (
                <div key={t.id} className="flex justify-between gap-2 text-xs">
                  <span className="truncate text-[#475569]">{fmtDate(t.date)} {t.counterparty || t.category}</span>
                  <span className={`tabular-nums shrink-0 font-medium ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                    {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {recv?.count > 0 && (
        <div data-testid="dashboard-receivables-widget" className={`bg-white border rounded-sm p-5 ${recv.overdue_count > 0 ? "border-[#DC2626]/40" : "border-[#E2E8F0]"}`}>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A] flex items-center gap-2">
              <HandCoins className="w-4 h-4 text-[#0D9488]" /> Nenaplaćeni izlazni računi
            </h2>
            <Link to="/nenaplaceno" data-testid="dashboard-receivables-link" className="text-sm text-[#0D9488] font-medium hover:underline underline-offset-2">
              Svi nenaplaćeni →
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mb-3">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Ukupno čeka naplatu</p>
              <p className="font-heading text-xl font-bold text-[#0D9488] tabular-nums" data-testid="dashboard-receivables-total">{fmtNum(recv.total_eur, "€")} <span className="text-sm font-normal text-[#64748B]">({recv.count} {plHr(recv.count, "račun", "računa", "računa")})</span></p>
            </div>
            {recv.overdue_count > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Od toga kasni</p>
                <p className="font-heading text-xl font-bold text-[#DC2626] tabular-nums" data-testid="dashboard-receivables-overdue">{fmtNum(recv.overdue_total_eur, "€")} <span className="text-sm font-normal text-[#64748B]">({recv.overdue_count} {plHr(recv.overdue_count, "račun", "računa", "računa")})</span></p>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            {recv.items.slice(0, 3).map((r, i) => (
              <div key={r.id} data-testid={`dashboard-receivable-${i}`} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-[#475569]">
                  {fmtDate(r.date)} · {r.counterparty || "—"}{r.invoice_number ? ` · br. ${r.invoice_number}` : ""}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {r.days_overdue > 0 && <span className="text-[11px] font-semibold text-[#DC2626] bg-[#DC2626]/10 rounded-sm px-1.5 py-0.5">kasni {r.days_overdue} {r.days_overdue % 10 === 1 && r.days_overdue % 100 !== 11 ? "dan" : "dana"}</span>}
                  <span className="font-semibold tabular-nums text-[#0D9488]">+{fmtNum(r.amount, r.currency === "EUR" ? "€" : "KM")}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-sm p-5">
          <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">Priljev / odljev po mjesecima (EUR)</h2>
          {data.monthly.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-[#64748B]" data-testid="chart-empty-state">
              Još nema transakcija — dodajte ih ručno ili uvezite izvod.
            </div>
          ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => [fmtNum(v, "€"), n === "inflow" ? "Priljev" : "Odljev"]} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="inflow" fill="#0D9488" radius={[2, 2, 0, 0]} name="inflow" />
                <Bar dataKey="outflow" fill="#DC2626" radius={[2, 2, 0, 0]} name="outflow" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
          <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">Zadnje transakcije</h2>
          <div className="space-y-3" data-testid="recent-transactions">
            {data.recent.length === 0 && <p className="text-sm text-[#64748B]">Nema transakcija.</p>}
            {data.recent.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {t.type === "naplata" ? (
                    <ArrowDownLeft className="w-4 h-4 text-[#0D9488] shrink-0" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4 text-[#DC2626] shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{t.counterparty || t.category}</p>
                    <p className="text-xs text-[#64748B] truncate" title={t.note || ""}>{fmtDate(t.date)}{t.note ? ` · ${t.note}` : ""}</p>
                  </div>
                </div>
                <p className={`text-sm font-semibold tabular-nums shrink-0 ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                  {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(() => {
        const active = (fleet?.items || []).filter((i) => i.count > 0);
        if (!active.length) return null;
        const top = active.filter((i) => i.net_eur > 0).slice(0, 3);
        const losing = active.filter((i) => i.net_eur < 0);
        return (
          <div data-testid="dashboard-vehicles-widget" className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h2 className="font-heading text-lg font-semibold text-[#0F172A] flex items-center gap-2">
                <Car className="w-4 h-4 text-[#0D9488]" /> Vozila — zarada {new Date().getFullYear()}.
              </h2>
              <Link to="/vozila" data-testid="dashboard-vehicles-link" className="text-sm text-[#0D9488] font-medium hover:underline underline-offset-2">
                Sva vozila →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Najbolja vozila</p>
                {top.length === 0 ? (
                  <p className="text-sm text-[#64748B]">Nijedno vozilo trenutno nije u plusu.</p>
                ) : (
                  <div className="space-y-2">
                    {top.map((v, i) => (
                      <div key={v.plate} data-testid={`dash-vehicle-top-${v.plate}`} className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-[#0F172A] flex items-center gap-2 min-w-0">
                          <span className="w-5 h-5 rounded-sm bg-[#0D9488]/10 text-[#0D9488] text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                          <span className="truncate">{v.plate}{v.label ? ` · ${v.label}` : ""}</span>
                        </span>
                        <span className="text-sm font-bold tabular-nums text-[#0D9488] shrink-0">+{fmtNum(v.net_eur, "€")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">U minusu</p>
                {losing.length === 0 ? (
                  <p className="text-sm text-[#64748B]">Nijedno vozilo nije u minusu.</p>
                ) : (
                  <div className="space-y-2">
                    {losing.map((v) => (
                      <div key={v.plate} data-testid={`dash-vehicle-loss-${v.plate}`} className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-[#0F172A] flex items-center gap-2 min-w-0">
                          <AlertTriangle className="w-3.5 h-3.5 text-[#DC2626] shrink-0" />
                          <span className="truncate">{v.plate}{v.label ? ` · ${v.label}` : ""}</span>
                        </span>
                        <span className="text-sm font-bold tabular-nums text-[#DC2626] shrink-0">{fmtNum(v.net_eur, "€")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-[#64748B] mt-4 pt-3 border-t border-[#F1F5F9] tabular-nums" data-testid="dashboard-fleet-totals">
              Flota ukupno: <span className="font-semibold text-[#0D9488]">+{fmtNum(fleet.totals.income_eur, "€")}</span> prihoda · <span className="font-semibold text-[#DC2626]">−{fmtNum(fleet.totals.expense_eur, "€")}</span> troškova · neto <span className={`font-bold ${fleet.totals.net_eur < 0 ? "text-[#DC2626]" : "text-[#0F172A]"}`}>{fmtNum(fleet.totals.net_eur, "€")}</span>
            </p>
          </div>
        );
      })()}

      <TransactionDialog open={txOpen} onOpenChange={setTxOpen} banks={banks} categories={categories} editing={null} onSaved={load} />
    </div>
  );
}
